import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, User, Users, ArrowUp, ArrowDown } from "lucide-react";

const COMPANIONS_OPTS = [
    { value: "solo", label: "Viene solo/a", icon: User },
    { value: "con_acompanantes", label: "Viene con acompañante/s", icon: Users },
];

const ARRIVAL_OPTS = [
    { value: "can_go_up", label: "Que suba", icon: ArrowUp },
    { value: "wait_at_reception", label: "Bajo a buscarla", icon: ArrowDown },
];

function ChoiceGroup({ value, options, onChange, testIdPrefix }) {
    return (
        <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid={`${testIdPrefix}-group`}>
            {options.map((opt) => {
                const selected = value === opt.value;
                const Icon = opt.icon;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => onChange(opt.value)}
                        data-testid={`${testIdPrefix}-${opt.value}`}
                        className={`rounded-md border p-3 transition-colors flex items-center gap-2 text-left ${
                            selected
                                ? "border-primary bg-primary-soft/40 text-primary"
                                : "border-border bg-card text-foreground hover:bg-primary-soft/20"
                        }`}
                    >
                        <Icon className={`h-4 w-4 shrink-0 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="text-sm font-medium leading-tight">{opt.label}</span>
                    </button>
                );
            })}
        </div>
    );
}

export default function CargarVisita() {
    const nav = useNavigate();
    const today = new Date().toISOString().slice(0, 10);
    const [fecha, setFecha] = useState(today);
    const [nombre, setNombre] = useState("");
    const [apellido, setApellido] = useState("");
    const [companionsMode, setCompanionsMode] = useState("solo");
    const [arrivalAction, setArrivalAction] = useState("can_go_up");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!nombre.trim() || !apellido.trim()) {
            return toast.error("Completá nombre y apellido");
        }
        setBusy(true);
        try {
            await api.post("/visitas", {
                nombre_visita: `${nombre.trim()} ${apellido.trim()}`,
                dni_visita: "",
                telefono_visita: "",
                fecha,
                hora_estimada: "",
                observaciones: "",
                companions_mode: companionsMode,
                arrival_action: arrivalAction,
            });
            toast.success("Visita cargada");
            nav("/mis-visitas");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-xl">
            <h1 className="text-xl font-heading font-bold tracking-tight mb-3">Cargar Visita</h1>

            <div className="space-y-1.5 mb-4">
                <Label className="text-xs">Fecha</Label>
                <Input
                    type="date"
                    value={fecha}
                    min={today}
                    onChange={(e) => setFecha(e.target.value || today)}
                    data-testid="vis-fecha"
                />
            </div>

            <div className="rounded-md border border-border bg-card p-3 mb-4">
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="space-y-1">
                        <Label className="text-[11px]">Nombre</Label>
                        <Input
                            data-testid="vis-nombre"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Juan"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px]">Apellido</Label>
                        <Input
                            data-testid="vis-apellido"
                            value={apellido}
                            onChange={(e) => setApellido(e.target.value)}
                            placeholder="Pérez"
                        />
                    </div>
                </div>

                <Label className="text-[11px] mb-1.5 block">¿Viene con alguien más?</Label>
                <ChoiceGroup
                    value={companionsMode}
                    options={COMPANIONS_OPTS}
                    onChange={setCompanionsMode}
                    testIdPrefix="vis-comp"
                />
            </div>

            <div className="mb-5">
                <Label className="text-xs mb-1.5 block">¿Qué hacemos cuando llegue?</Label>
                <ChoiceGroup
                    value={arrivalAction}
                    options={ARRIVAL_OPTS}
                    onChange={setArrivalAction}
                    testIdPrefix="vis-arr"
                />
            </div>

            <Button
                onClick={submit}
                disabled={busy}
                className="w-full"
                data-testid="vis-submit"
            >
                {busy
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cargando…</>
                    : "Cargar visita"}
            </Button>
        </div>
    );
}
